import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Handshake, CheckCircle2 } from "lucide-react";
import { KenyaStripe } from "@/components/Logo";

const EMPTY = { brand: "", contact_name: "", email: "", phone: "", package_interest: "league", message: "" };

const INTENT_PRESETS = {
  sponsorship: {
    title: "Sponsor Mara Padel",
    description: "Reach 16 franchise teams, broadcast-grade live coverage and a KES 3.97M prize purse audience.",
    cta: "Send Sponsorship Inquiry",
    default_package: "league",
    brand_label: "Brand / Company *",
  },
  franchise: {
    title: "Own a Mara Padel Franchise",
    description: "Apply for one of the 16 franchise slots. Build your squad in the live auction and compete for the KES 3.97M prize purse.",
    cta: "Submit Franchise Application",
    default_package: "team",
    brand_label: "Proposed Team / Company *",
  },
};

export default function SponsorInquiryDialog({ children, defaultIntent = "sponsorship" }) {
  const preset = INTENT_PRESETS[defaultIntent] || INTENT_PRESETS.sponsorship;
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ ...EMPTY, package_interest: preset.default_package });
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!form.brand || !form.email) return toast.error("Brand name and email are required");
    setSubmitting(true);
    try {
      await api.post("/sponsor-inquiries", { ...form, intent: defaultIntent });
      setDone(true);
    } catch (e) { toast.error(formatApiError(e)); }
    setSubmitting(false);
  };

  const close = () => { setOpen(false); setTimeout(() => { setDone(false); setForm({ ...EMPTY, package_interest: preset.default_package }); }, 300); };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) close(); }}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent data-testid="sponsor-inquiry-dialog" className="bg-[#11241b] border-white/10 text-white max-w-lg">
        {done ? (
          <div className="py-6 text-center" data-testid="sponsor-inquiry-done">
            <CheckCircle2 className="w-16 h-16 mx-auto text-[#00A86B] mb-3" />
            <DialogTitle className="font-anton text-3xl uppercase tracking-tight">Inquiry Sent</DialogTitle>
            <p className="text-zinc-400 mt-2 text-sm">Thank you for your interest. The Mara Padel commercial team will reach out within 48 hours.</p>
            <Button onClick={close} className="mt-5 bg-[#00A86B] hover:bg-[#00935A] text-white">Close</Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <div className="flex items-center gap-2">
                <Handshake className="w-5 h-5 text-[#00A86B]" />
                <DialogTitle className="font-anton text-3xl uppercase tracking-tight">{preset.title}</DialogTitle>
              </div>
              <KenyaStripe className="mt-2 w-24" />
              <DialogDescription className="text-zinc-400">
                {preset.description}
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <Field label={preset.brand_label}>
                <Input data-testid="sponsor-brand" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} className="bg-[#060d09] border-white/10 text-white" />
              </Field>
              <Field label="Your Name">
                <Input data-testid="sponsor-contact-name" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className="bg-[#060d09] border-white/10 text-white" />
              </Field>
              <Field label="Email *">
                <Input data-testid="sponsor-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-[#060d09] border-white/10 text-white" />
              </Field>
              <Field label="Phone">
                <Input data-testid="sponsor-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-[#060d09] border-white/10 text-white" />
              </Field>
              <Field label="Package of Interest" full>
                <Select value={form.package_interest} onValueChange={(v) => setForm({ ...form, package_interest: v })}>
                  <SelectTrigger data-testid="sponsor-package" className="bg-[#060d09] border-white/10 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#11241b] border-white/10 text-white">
                    <SelectItem value="title">Title Sponsor (Naming Rights)</SelectItem>
                    <SelectItem value="league">League Sponsor (Left Chest)</SelectItem>
                    <SelectItem value="team">Team Sponsor</SelectItem>
                    <SelectItem value="banner">Court Banner</SelectItem>
                    <SelectItem value="stall">Commercial Stall</SelectItem>
                    <SelectItem value="other">Custom Activation</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Message" full>
                <Textarea data-testid="sponsor-message" rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Tell us about your activation goals" className="bg-[#060d09] border-white/10 text-white" />
              </Field>
            </div>
            <Button data-testid="btn-submit-sponsor" disabled={submitting} onClick={submit} className="mt-4 w-full bg-[#00A86B] hover:bg-[#00935A] text-white font-bold uppercase tracking-widest">
              {submitting ? "Sending…" : preset.cta}
            </Button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <label className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
